import { computed, type Ref } from 'vue';
import type { IColumnDefinition, TableOptions } from '../types';

interface UseColumnParams {
  column: IColumnDefinition;
  options: TableOptions;
  editing?: Ref<boolean>;
}

const ALIGN_CLASS_MAP: Record<string, string> = {
  left: 'text-start',
  right: 'text-end',
  center: 'text-center',
  start: 'text-start',
  end: 'text-end',
};

export function useColumn(params: UseColumnParams) {
  const columnStyle = computed(() => {
    let width = params.column.width;

    if (!width) {
      width = params.column.minWidth
        ? typeof params.column.minWidth === 'number'
          ? `${params.column.minWidth}px`
          : params.column.minWidth
        : '150px';
    }

    const style: Record<string, any> = {
      width: width,
      minWidth: params.column.minWidth,
      maxWidth: width,
    };

    if (params.column.frozen) {
      style.position = 'sticky';
      if (
        params.column.sticky === 'left' ||
        params.column.name === 'row-selector' ||
        params.column.name === 'row-header'
      ) {
        style.left = 0;
        style.zIndex = 3;
      }
      if (
        params.column.sticky === 'right' ||
        params.column.name === 'row-menu'
      ) {
        style.right = 0;
        style.zIndex = 3;
      }
    }

    return style;
  });

  const columnClasses = computed(() => {
    const classes: Record<string, boolean> = {};

    if (params.column.frozen) {
      if (
        params.column.sticky === 'left' ||
        params.column.name === 'row-selector' ||
        params.column.name === 'row-header'
      ) {
        classes['sticky-l'] = true;
      }
      if (
        params.column.sticky === 'right' ||
        params.column.name === 'row-menu'
      ) {
        classes['sticky-r'] = true;
      }
    }

    let align = params.column.textAlign;
    if (!align) {
      const type = params.column.type;
      if (type === 'number') {
        align = 'right';
      } else {
        align = params.options.textAlign || 'left';
      }
    }

    if (align) {
      const cls = ALIGN_CLASS_MAP[align] ?? 'text-start';
      classes[cls] = true;
    }

    return classes;
  });

  const displayCellHeader = computed(() => {
    const style = params.options.columnHeaderStyle;
    if (style === 'cellHeader') {
      return true;
    }
    if (style === 'cellheaderWhenEditing' && params.editing?.value) {
      return true;
    }
    return false;
  });

  return {
    columnStyle,
    columnClasses,
    displayCellHeader,
  };
}
